'use client'

import { Slideshow } from '@fukku/slideshow/Slideshow'
import {
	SlideshowAnimation,
	type SlideshowFromDirection,
} from '@fukku/slideshow/types/Slideshow'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import replacePlaceholders from 'labels/lib/replacePlaceholders'
import SmartCropImage from 'media/components/SmartCropImage/SmartCropImage'
import { sendSwipeImagesEvent } from 'product/analytics/sendSwipeImagesEvent'
import type { ProductImage } from 'product/types'
import { PdpImageLinkWrapper } from 'product-card/components/shared/ProductImage/PdpImageLinkWrapper/PdpImageLinkWrapper'
import { dataTestIds } from 'product-card/constants/dataTestIds'
import { useAnimationImagesLoad } from 'product-card/hooks/useAnimationImagesLoad'
import { useProductCard } from 'product-card/hooks/useProductCard'
import { useProductCardAnimationContext } from 'product-card/hooks/useProductCardAnimationContext'
import { useProductCardConsumerLayoutContext } from 'product-card/hooks/useProductCardConsumerLayoutContext'
import { useProductImageSizes } from 'product-card/hooks/useProductImageSizes/useProductImageSizes'
import { useSelectedColor } from 'product-card/hooks/useSelectedColor/useSelectedColor'
import { useShowAsFeatured } from 'product-card/hooks/useShowAsFeatured/useShowAsFeatured'
import { ImageDisplayType, ProductCardAnimation } from 'product-card/types'
import { getProductLinkLabel } from 'product-card/utils/getProductLinkLabel/getProductLinkLabel'
import { useCallback, useMemo, useRef } from 'react'
import { cx } from 'utils/cx'
import { env } from 'utils/envManager'
import { some } from 'utils/some/some'

import styles from '../ProductImage.module.scss'

interface ProductImageSlideshowProps {
	readonly images: ProductImage[]
}

const FIRST_IMAGE = 0
const SECOND_IMAGE = 1

export const ProductImageSlideshow = ({
	images,
}: ProductImageSlideshowProps) => {
	const {
		dataTestId,
		draggable,
		priority,
		product,
		selectedColorId,
		lookId,
		location,
	} = useProductCard()

	const { selectedColor } = useSelectedColor()

	const { animation, onAnimationEnd } = useProductCardAnimationContext()

	const { hasDotsIndicator } = useProductCardConsumerLayoutContext()

	const firstImageRef = useRef<HTMLImageElement | null>(null)

	const secondImageRef = useRef<HTMLImageElement | null>(null)

	const { isFirstImageReady, areImagesReadyForAnimation, handleImageLoaded } =
		useAnimationImagesLoad(images, secondImageRef)

	const showAsFeatured = useShowAsFeatured()

	const { t } = useLabels()

	const sizes = useProductImageSizes()

	const handleSwipe = useCallback(
		(direction: SlideshowFromDirection) => {
			sendSwipeImagesEvent({
				productId: product.id,
				colorId: selectedColorId,
				count: images.length,
				location,
				direction,
			})
		},
		[product.id, selectedColorId, images.length, location]
	)

	const getImagePriority = useCallback(
		(index: number) =>
			some(
				priority && index === FIRST_IMAGE,
				animation !== ProductCardAnimation.FadeIn &&
					[FIRST_IMAGE, SECOND_IMAGE].includes(index)
			),
		[animation, priority]
	)

	const getImageRef = useCallback((index: number) => {
		if (index === FIRST_IMAGE) {
			return firstImageRef
		}
		if (index === SECOND_IMAGE) {
			return secondImageRef
		}
		return null
	}, [])

	// Apply fade in animation if there is any animation defined
	const fadeInAnimation = isFirstImageReady ? styles.fadeIn : styles.invisible

	const initialAnimation = useMemo<SlideshowAnimation | undefined>(() => {
		if (!areImagesReadyForAnimation) {
			return undefined
		}
		if (animation === ProductCardAnimation.ShortSlideshow) {
			return SlideshowAnimation.Short
		}
		if (animation === ProductCardAnimation.LongSlideshow) {
			return SlideshowAnimation.Long
		}
		return undefined
	}, [areImagesReadyForAnimation, animation])

	const [firstImage] = images

	const slideshowAriaLabel = replacePlaceholders(
		t('accessibility.productCard.images.carrousel'),
		{
			1: product.name,
			2: selectedColor?.label ?? selectedColorId,
		}
	)

	return (
		<div className={fadeInAnimation} data-testid='productCard.images'>
			<Slideshow
				ariaLabel={slideshowAriaLabel}
				dataTestId={dataTestIds.productCardSlideshow(dataTestId)}
				hasPreload
				initialAnimation={initialAnimation}
				onAnimationEnd={onAnimationEnd}
				onSwipe={handleSwipe}
				hasDotsIndicator={hasDotsIndicator}
				labels={{
					previous: t('accessibility.image.previous.button'),
					next: t('accessibility.image.next.button'),
				}}
			>
				{images.map((image, index) => (
					<PdpImageLinkWrapper
						key={image.alt}
						className={cx(
							styles.imageWrapper,
							showAsFeatured && styles.featured
						)}
						imageDisplayType={ImageDisplayType.Slider}
						isSEORelevant={index === FIRST_IMAGE}
					>
						<meta
							itemProp='image'
							content={
								new URL(firstImage.img, env.NEXT_PUBLIC_SITE_BASE_URL).href
							}
						/>
						<SmartCropImage
							ref={getImageRef(index)}
							data-testid={dataTestIds.productCardImage(dataTestId, index)}
							alt={getProductLinkLabel({
								productId: product.id,
								productName: image.alt,
								selectedColorLabel: selectedColor?.label ?? selectedColorId,
								lookId,
							})}
							src={image.img}
							sizes={sizes}
							priority={getImagePriority(index)}
							showAsFeatured={showAsFeatured}
							fill
							draggable={draggable}
							onLoad={() => handleImageLoaded(image)}
						/>
					</PdpImageLinkWrapper>
				))}
			</Slideshow>
		</div>
	)
}
